import { useEffect, useMemo, useState } from "react";
import { Dropdown, Nav, Tab } from "react-bootstrap"
import { useTranslation } from "react-i18next";
import SvgShuffle from "components/svg/SvgShuffle";
import SvgUsers from "components/svg/SvgUsers";
import SvgPackage from "components/svg/SvgPackage";
import ObjectTabProducts from "./Tabs/ObjectTabProducts";
import ObjectTabServices from "./Tabs/ObjectTabServices";
import ObjectTabOther from "./Tabs/ObjectTabOther";
import SvgEdit from "components/svg/SvgEdit";
import ObjectTabWarranties from "./Tabs/ObjectTabWarranties";
import ObjectTabDescription from "./Tabs/ObjectTabDescription";
import ObjectTabCustomer from "./Tabs/ObjectTabCustomer";
import ObjectTabDocuments from "./Tabs/ObjectTabDocuments";
import ObjectTabEvents from "./Tabs/ObjectTabEvents";
import ObjectTabFiles from "./Tabs/ObjectTabFiles";
import ObjectDiscussion from "./Discussion";
import DiscussionUnreadBadge from "./Discussion/DiscussionUnreadBadge";
import { useDiscussionUnread } from "./Discussion/model";
import { useObjectCard } from "./context/ObjectCardProvider";
import SvgMenu from "components/svg/SvgMenu";
import SpinnerBlock from "components/others/SpinnerBlock";
import ObjectDetails from "../ObjectDetails";
import SolarIcon from 'components/icons/SolarIcon';

const ObjectCardTabs = () => {
     
    const { t } = useTranslation(['Objects'])
    const { vObject, fetchObject, showSpinner, isEditLocked, onlySale, onlyService, isSaleLike, objectId } = useObjectCard();
    const [activeKey, setActiveKey] = useState('documents');
    const hasEvents = vObject?.events?.length > 0;
    const { unreadCount, clearUnread } = useDiscussionUnread(Number(objectId) || 0, 'office', !isSaleLike);

    const moreItems = useMemo(() => ([
        {
            key: 'description',
            label: t('infoItems.objectDescription'),
            icon: <SvgEdit height={14} className="me-1" />,
        },
        ...(!onlyService ? [{ key: 'warranties', label: t('infoItems.objectWarranties'), icon: <SolarIcon name="shield-check" className="me-1 fs-14" /> }] : []),
        {
            key: 'customer',
            label: t('infoItems.objectCustomer'),
            icon: <SolarIcon name="person-vcard" className="me-1 fs-14" />,
        },
        ...(!isSaleLike ? [{
            key: 'specification',
            label: t('infoItems.objectSpecification'),
            icon: <SolarIcon name="card-list" className="me-1 fs-14" />,
        }] : []),
        {
            key: 'files',
            label: t('infoItems.objectFiles'),
            icon: <SolarIcon name="paperclip" className="me-1 fs-14" />,
        },
        ...(hasEvents ? [{
            key: 'events',
            label: t('infoItems.objectEvents'),
            icon: <SvgMenu height={14} className="me-1" />,
        }] : []),
        ...(!isSaleLike ? [{
            key: 'discussion',
            label: t('infoItems.objectDiscussion'),
            icon: <SolarIcon name="chat-left-text" className="me-1 fs-14" />,
        }] : []),
    ]), [t, isSaleLike, hasEvents, onlyService]);

    const activeMore = moreItems.find((item) => item.key === activeKey);
    const moreLabel = (label) => `... ${label}`;

    useEffect(() => {
        const allowed = new Set([
            'documents',
            ...(!onlyService ? ['products'] : []),
            ...(!onlySale ? ['services'] : []),
            ...(!isSaleLike ? ['extra', 'other'] : []),
            ...moreItems.map((item) => item.key),
        ]);
        if (!allowed.has(activeKey)) setActiveKey('documents');
    }, [activeKey, onlyService, onlySale, isSaleLike, hasEvents, moreItems]);

    return (
        <div>
            <SpinnerBlock show={showSpinner} onlyBlur={true}/>
            <div className="px-0 pt-2">
                <Tab.Container
                    activeKey={activeKey}
                    onSelect={(key) => { if (key) setActiveKey(key); }}
                >
                    <Nav variant="underline" className="mb-3 invoice-tab border-bottom">
                        <Nav.Item>
                            <Nav.Link eventKey="documents">
                                <SolarIcon name="file-earmark-text" className="me-1 fs-14" />
                                {t('infoItems.objectDocuments')}
                            </Nav.Link>
                        </Nav.Item>

                        {!onlyService ? (
                        <Nav.Item>
                            <Nav.Link eventKey="products">
                                <SvgPackage height={14} className="me-1"/>
                                {t('infoItems.objectMaterials')}
                            </Nav.Link>
                        </Nav.Item>
                        ) : null}

                        {!onlySale ? (
                        <Nav.Item>
                            <Nav.Link eventKey="services">
                                <SvgUsers height={14} className="me-1"/>
                                {t('infoItems.objectServices')}
                            </Nav.Link>
                        </Nav.Item>
                        ) : null}

                        {!isSaleLike ? (
                        <Nav.Item>
                            <Nav.Link eventKey="extra">
                                <SolarIcon name="inbox" className="me-1 fs-14" />
                                {t('infoItems.objectExtra')}
                            </Nav.Link>
                        </Nav.Item>
                        ) : null}

                        {!isSaleLike ? (
                        <Nav.Item>
                            <Nav.Link eventKey="other">
                                <SvgShuffle height={14} className="me-1"/>
                                {t('infoItems.objectOther')}
                            </Nav.Link>
                        </Nav.Item>
                        ) : null}

                        <Dropdown
                            as={Nav.Item}
                            align="end"
                            onSelect={(key) => { if (key) setActiveKey(String(key)); }}
                        >
                            <Dropdown.Toggle
                                as={Nav.Link}
                                bsPrefix="nav-link"
                                className="object-card-more-tab"
                                id="object-card-more-tab"
                                active={Boolean(activeMore)}
                            >
                                {activeMore?.icon}
                                {activeMore ? moreLabel(activeMore.label) : t('infoItems.objectMore')}
                                {!isSaleLike && activeKey !== 'discussion' ? (
                                    <DiscussionUnreadBadge count={unreadCount} />
                                ) : null}
                                <span className="object-card-more-caret" aria-hidden="true" />
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                {moreItems.map((item) => (
                                    <Dropdown.Item
                                        key={item.key}
                                        eventKey={item.key}
                                        active={activeKey === item.key}
                                    >
                                        {item.icon}
                                        {item.label}
                                        {item.key === 'discussion' ? (
                                            <DiscussionUnreadBadge count={unreadCount} />
                                        ) : null}
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                    </Nav>

                    <Tab.Content>
                        <Tab.Pane eventKey="documents">
                            <ObjectTabDocuments/>
                        </Tab.Pane>

                        {!onlyService ? (
                        <Tab.Pane eventKey="products">
                            <ObjectTabProducts/>
                        </Tab.Pane>
                        ) : null}

                        {!onlySale ? (
                        <Tab.Pane eventKey="services">
                            <ObjectTabServices/>
                        </Tab.Pane>
                        ) : null}

                        {!isSaleLike ? (
                        <Tab.Pane eventKey="extra">
                            <ObjectTabServices mode="other"/>
                        </Tab.Pane>
                        ) : null}

                        {!isSaleLike ? (
                        <Tab.Pane eventKey="other">
                            <ObjectTabOther/>
                        </Tab.Pane>
                        ) : null}

                        <Tab.Pane eventKey="description">
                            <ObjectTabDescription/>
                        </Tab.Pane>

                        {!onlyService && (
                        <Tab.Pane eventKey="warranties" mountOnEnter unmountOnExit transition={false}>
                            <ObjectTabWarranties />
                        </Tab.Pane>
                        )}
                        <Tab.Pane eventKey="customer">
                            <ObjectTabCustomer/>
                        </Tab.Pane>

                        {!isSaleLike ? (
                        <Tab.Pane eventKey="specification">
                            <ObjectDetails
                                objectId={vObject?.object?.ID}
                                objectDetails={vObject?.object?.objectDelails}
                                readOnly={isEditLocked}
                                onSaved={fetchObject}
                            />
                        </Tab.Pane>
                        ) : null}

                        <Tab.Pane eventKey="files">
                            <ObjectTabFiles/>
                        </Tab.Pane>

                        {hasEvents ? (
                        <Tab.Pane eventKey="events">
                            <ObjectTabEvents/>
                        </Tab.Pane>
                        ) : null}

                        {!isSaleLike ? (
                        <Tab.Pane eventKey="discussion" mountOnEnter unmountOnExit>
                            <ObjectDiscussion
                                objectId={Number(objectId) || 0}
                                channel="office"
                                onSeen={clearUnread}
                            />
                        </Tab.Pane>
                        ) : null}
                    </Tab.Content>
                </Tab.Container>
            </div> 
        </div>
    )
}

export default ObjectCardTabs;
